import {
  TEntityName,
  TEntityUntagged,
} from '@obvious.tech/constellation/Entity'
import { AnyFail, Fail, UnknownFail } from '@obvious.tech/util/Fail'
import { err, Result } from 'neverthrow'
import {
  TUseReferenceAutocompleteParam,
  useReferenceAutocomplete,
} from './useReferenceAutocomplete'
import { TReference } from 'src/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { getPropUntyped, setPropUntyped } from '@obvious.tech/util/record'
import { Abortable } from '@obvious.tech/util/async'
import { useLatest } from '@obvious.tech/util/react/hooks'
import { useTranslation } from 'react-i18next'
import { isPrintable } from '@obvious.tech/util/string'
import { TMatchInputCb } from 'src/components/inputs/Autocomplete/AutocompleteInput'

type TValueTArg = Record<string, unknown> | string

export type TInvalidOptionReason = {
  code: 'missing_identifier' | 'missing_name' | 'hydration_failed' | 'generic'
  error: AnyFail
}
export type TOption<
  Value extends TValueTArg = TValueTArg,
  Target extends TEntityName = TEntityName,
> = (
  | {
      _tag: 'invalid'
      reason: TInvalidOptionReason
      value?: never
      serverValue?: unknown
    }
  | {
      _tag: 'valid'
      value: Value
      serverValue?: never
    }
) & {
  id?: string
  label?: string
  entity?: TEntityUntagged<Target>
  entityType: Target
}

export type TGetOptionLabel<
  Value extends TValueTArg = TValueTArg,
  Target extends TEntityName = TEntityName,
> = (option: TOption<Value, Target>) => string

export type TUseReferenceSelectionParam<
  Value extends TValueTArg = TValueTArg,
  Target extends TEntityName = TEntityName,
> = {
  reference: TReference<Value, Target>
  initialValue?: Value | null
  onChange?: (value: Value | null) => void
  getOptionLabel?: TGetOptionLabel<Value, Target>
}

export type TUseReferenceSelectionReturn<
  Value extends TValueTArg = TValueTArg,
  Target extends TEntityName = TEntityName,
> = {
  loading: boolean

  inputText: string

  selectedOption: TOption<Value, Target> | null

  options: Result<Array<TOption<Value, Target>>, UnknownFail>
  optionsLoading: boolean

  handleInputChange: (value: string) => void
  handleChange: (option: TOption<Value, Target> | null) => void
  getOptionLabel: TGetOptionLabel<Value, Target>
  getOptionDisabled: (option: TOption<Value, Target>) => boolean
  matchInputToOption: TMatchInputCb<TOption<Value, Target>>
}

export const useReferenceSelection = <
  Value extends TValueTArg = TValueTArg,
  Target extends TEntityName = TEntityName,
>({
  reference,
  initialValue = null,
  onChange,
  getOptionLabel: getOptionLabelFromProps,
}: TUseReferenceSelectionParam<Value, Target>): TUseReferenceSelectionReturn<
  Value,
  Target
> => {
  const { search, entities, entitiesLoading, getById } =
    useReferenceAutocomplete<Target>(
      reference as TUseReferenceAutocompleteParam<Target>,
    )
  const { t } = useTranslation()

  const [loading, setLoading] = useState<boolean>(true)
  const [initialOption, setInitialOption] = useState<TOption<
    Value,
    Target
  > | null>(
    initialValue !== null
      ? toOption<Value, Target>({
          reference,
          sourceType: 'initialValue',
          source: initialValue,
        })
      : null,
  )
  const [selectedOption, setselectedOption] = useState<TOption<
    Value,
    Target
  > | null>(initialOption)
  const latestSelectedOption = useLatest(selectedOption)
  const getOptionLabel: TGetOptionLabel<Value, Target> = useCallback(
    option => {
      if (typeof getOptionLabelFromProps === 'function')
        return getOptionLabelFromProps(option)

      if (isPrintable(option.label)) return option.label

      if (isPrintable(option.id)) return option.id

      return t('global.noContent')
    },
    [getOptionLabelFromProps, t],
  )
  const [inputText, setInputText] = useState<string>(
    initialOption !== null ? optionToInputValue(initialOption) : '',
  )
  const latestInputText = useLatest(inputText)

  useEffect(() => {
    const unhydratedOption =
      initialValue !== null
        ? toOption<Value, Target>({
            reference,
            sourceType: 'initialValue',
            source: initialValue,
          })
        : null

    const beingHydratedId = unhydratedOption?.id
    if (typeof beingHydratedId !== 'string') {
      setLoading(false)
      return
    }

    setLoading(true)
    const controller = new AbortController()
    void Abortable.match(
      Abortable.from(getById(beingHydratedId), controller.signal),
      {
        onContinue(result) {
          const newInitialOption = result.match<TOption<Value, Target>>(
            entity => {
              return toOption({
                reference,
                sourceType: 'entity',
                source: entity,
              })
            },
            error => {
              return {
                _tag: 'invalid',
                reason: {
                  code: 'hydration_failed',
                  error,
                },
                entityType: reference.target,
                entity: unhydratedOption?.entity,
                id: unhydratedOption?.id,
                label: unhydratedOption?.label,
                serverValue: unhydratedOption?.serverValue,
              }
            },
          )
          setInitialOption(newInitialOption)

          if (latestSelectedOption.current?.id !== beingHydratedId) return
          setselectedOption(newInitialOption)

          if (unhydratedOption === null) return
          const initialInputText = optionToInputValue(unhydratedOption)

          if (latestInputText.current !== initialInputText) return

          setInputText(optionToInputValue(newInitialOption))
        },
        onAbort() {},
        onFinally() {
          setLoading(false)
        },
      },
    )

    return () => controller.abort()
  }, [
    initialValue,
    getById,
    getOptionLabel,
    latestInputText,
    latestSelectedOption,
    reference,
  ])

  const handleChange = useCallback(
    (option: TOption<Value, Target> | null): void => {
      if (option?._tag === 'invalid') return

      setselectedOption(option)
      onChange?.(option?.value ?? null)
    },
    [onChange],
  )

  const handleInputChange = useCallback(
    (value: string) => {
      setInputText(value)
      void search(value)
    },
    [search],
  )

  const options = useMemo(() => {
    return entities.map(entities => {
      const options = entities.reduce<Array<TOption<Value, Target>>>(
        (acc, entity) => {
          const option = toOption({
            reference,
            sourceType: 'entity',
            source: entity,
          })
          if (typeof option.id === 'string') acc.push(option)

          return acc
        },
        [],
      )

      if (
        typeof initialOption?.id !== 'string' ||
        options.some(it => it.id === initialOption.id)
      )
        return options

      options.unshift(initialOption)

      return options
    })
  }, [entities, reference, initialOption])

  const matchInputToOption = useCallback<TMatchInputCb<TOption<Value, Target>>>(
    (input, options) => {
      if (input.trim() === '') return { _tag: 'empty' }

      type TMatcher = (input: string, option: TOption<Value, Target>) => boolean
      const matchers: TMatcher[] = [
        (input, option) => input === option.label,
        (input, option) => input === option.id,
      ]

      const matchingOptions = options.filter(option =>
        matchers.some(matcher => matcher(input, option)),
      )

      if (matchingOptions.length === 1)
        return { _tag: 'match', option: matchingOptions[0] }

      if (matchingOptions.length > 1)
        return { _tag: 'multiple-matches', options: matchingOptions }

      return { _tag: 'no-match', input }
    },
    [],
  )

  return useMemo(
    (): TUseReferenceSelectionReturn<Value, Target> => ({
      loading,
      inputText,
      selectedOption,
      options,
      optionsLoading: entitiesLoading,
      getOptionLabel,
      getOptionDisabled,
      handleInputChange,
      handleChange,
      matchInputToOption,
    }),
    [
      loading,
      inputText,
      selectedOption,
      options,
      entitiesLoading,
      getOptionLabel,
      handleInputChange,
      handleChange,
      matchInputToOption,
    ],
  )
}

function optionToInputValue<
  Value extends TValueTArg,
  Target extends TEntityName,
>(option: TOption<Value, Target>): string {
  return option.label ?? option.id ?? '<unknown>'
}

function toOption<Value extends TValueTArg, Target extends TEntityName>({
  reference,
  sourceType,
  source,
}: {
  reference: TReference<Value, Target>
} & (
  | {
      sourceType: 'entity'
      source: TEntityUntagged<Target>
    }
  | {
      sourceType: 'initialValue'
      source: Value
    }
)): TOption<Value, Target> {
  const {
    idKeyPath = null,
    labelKeyPath,
    targetIdKeyPath = 'id',
    targetLabelKeyPath = 'label',
  } = reference

  let sourceIdKeyPath: string | null | undefined
  let sourceLabelKeyPath: string | null | undefined
  let entity: TEntityUntagged<Target>
  if (sourceType === 'entity') {
    sourceIdKeyPath = targetIdKeyPath
    sourceLabelKeyPath = targetLabelKeyPath
    entity = source
  } else {
    sourceIdKeyPath = idKeyPath
    sourceLabelKeyPath = labelKeyPath
  }

  const idResult =
    sourceIdKeyPath !== undefined
      ? getPropUntyped(source, sourceIdKeyPath, z.string().min(1)).mapErr(
          error => ({
            code: 'missing_identifier' as const,
            error,
          }),
        )
      : err<string, TInvalidOptionReason>({
          code: 'missing_identifier',
          error: new Fail('unable to retrieve id'),
        })

  const labelResult =
    sourceLabelKeyPath !== undefined
      ? getPropUntyped(source, sourceLabelKeyPath, z.string().min(1))
      : err({
          code: 'missing_name',
          error: new Fail('unable to retrieve label or name'),
        })

  const valueResult = ((): Result<Value, TInvalidOptionReason> => {
    if (labelKeyPath === null)
      return labelResult as Result<Value, TInvalidOptionReason>
    if (idKeyPath === null)
      return idResult as Result<Value, TInvalidOptionReason>
    return idResult.map(id =>
      getObjectReference({
        reference,
        id,
        label: labelResult.unwrapOr(undefined),
      }),
    )
  })()

  return valueResult.match<TOption<Value, Target>>(
    value => {
      return {
        _tag: 'valid',
        id: idResult.unwrapOr(undefined),
        label: labelResult.unwrapOr(undefined),
        value,
        entityType: reference.target,
        entity,
      }
    },
    reason => ({
      _tag: 'invalid',
      reason,
      id: idResult.unwrapOr(undefined),
      label: labelResult.unwrapOr(undefined),
      serverValue: sourceType === 'initialValue' ? source : undefined,
      entityType: reference.target,
    }),
  )
}

function getObjectReference<
  Value extends TValueTArg,
  Target extends TEntityName,
>({
  reference,
  id,
  label,
}: {
  reference: TReference<Value, Target>
  id: string
  label: string | undefined
}): Value {
  const { idKeyPath = null, labelKeyPath } = reference

  const withId = setPropUntyped(null, idKeyPath, id) as Value
  if (labelKeyPath === undefined) return withId

  return setPropUntyped(withId, labelKeyPath, label) as Value
}

const getOptionDisabled: TUseReferenceSelectionReturn['getOptionDisabled'] =
  option => option._tag === 'invalid'
